import { NavigationItem } from '@/config/navigations';
import { paths } from '@/constants/paths';
import { normalizePath } from '@/utils/appMenuFilter';

/**
 * Advisor navigation utilities.
 *
 * Two related concerns live here, both keyed off the backend-driven menu:
 *  - `resolveAdvisorNavRoute`: normalize a legacy/inconsistent backend route to
 *    its canonical `paths.advisor.*` value.
 *  - `filterNavItemsForAdvisor`: drop nav items the Advisor role should not see.
 */

/* -------------------------------------------------------------------------- */
/* Route normalization                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Route-normalization layer for the Advisor role.
 *
 * Advisor keeps the backend-driven sidebar, so nav routes are minted from
 * `UserInfo.Menu` (legacy/inconsistent shapes like `/Advisor/Wealth/Accounts`,
 * `/advisor/businessintelligence/insights`, `/Documents/DocumentVault`). The new
 * router exposes clean `/advisor/*` paths, so every backend-derived advisor route
 * must be mapped to its canonical `paths.advisor.*` value before it reaches the
 * router or the permission guard.
 *
 * Strategy: match on the trailing path segment (case/punctuation-insensitive).
 * Advisor leaf names are unique enough that a trailing-segment map is sufficient.
 * The function is idempotent (canonical input maps to itself) and returns the
 * input unchanged for unknown segments.
 */

const norm = (segment: string): string =>
  segment.toLowerCase().replace(/[^a-z]/g, '');

/** trailing path segment (normalized) -> canonical `/advisor/*` path */
const ADVISOR_ROUTE_TABLE: Record<string, string> = {
  dashboard: paths.advisor.dashboard,

  // Clients
  clients: paths.advisor.clients.index,
  prospects: paths.advisor.clients.prospects,
  activity: paths.advisor.clients.activity,
  content: paths.advisor.clients.content,

  // Client's Wealth
  wealth: paths.advisor.clientsWealth.accounts,
  clientswealth: paths.advisor.clientsWealth.index,
  accounts: paths.advisor.clientsWealth.accounts,
  holdings: paths.advisor.clientsWealth.holdings,
  assetallocation: paths.advisor.clientsWealth.assetAllocation,
  // Backend labels Asset Allocation as "Portfolio Diversification".
  diversification: paths.advisor.clientsWealth.assetAllocation,
  balancesheet: paths.advisor.clientsWealth.balanceSheet,

  // Business Intelligence / Analytics
  analytics: paths.advisor.businessIntelligence.insights,
  businessintelligence: paths.advisor.businessIntelligence.index,
  bi: paths.advisor.businessIntelligence.index,
  insights: paths.advisor.businessIntelligence.insights,
  opportunities: paths.advisor.businessIntelligence.opportunities,
  risks: paths.advisor.businessIntelligence.risks,
  todolist: paths.advisor.businessIntelligence.toDoList,
  metrics: paths.advisor.businessIntelligence.metrics,
  aumheldaway: paths.advisor.businessIntelligence.aumHeldAway,
  aum: paths.advisor.businessIntelligence.aumHeldAway,

  // Documents
  documents: paths.advisor.documents.documentVault,
  documentvault: paths.advisor.documents.documentVault,
  notes: paths.advisor.documents.notes,
  intelligence: paths.advisor.documents.intelligence,

  // Reports
  reports: paths.advisor.reports,

  // Configuration. The section parent maps to the section index (a redirect
  // route to the first granted child) — NOT to a specific sub-page, so a
  // sub-page the backend didn't grant is never implicitly permitted through
  // the parent's route.
  configuration: paths.advisor.configuration.index,
  clientportal: paths.advisor.configuration.advisorAndClientPortals,
  advisorandclientportals: paths.advisor.configuration.advisorAndClientPortals,
  integrations: paths.advisor.configuration.integrations,
  legal: paths.advisor.configuration.legal.index,
  disclaimer: paths.advisor.configuration.legal.disclaimer,
  groupsandusers: paths.advisor.configuration.groupsAndUsers,
  // Backend emits Groups and Users as separate items; both land on the
  // combined Groups & Users page.
  groups: paths.advisor.configuration.groupsAndUsers,
  users: paths.advisor.configuration.groupsAndUsers,
  managedaccounts: paths.advisor.configuration.managedAccounts.index,
  datafeeds: paths.advisor.configuration.dataFeeds.managedAccounts,
  teammember: paths.advisor.configuration.teamMember,
  teammembers: paths.advisor.configuration.teamMember,
  team: paths.advisor.configuration.teamMember,
};

/**
 * Map any backend-derived advisor route to its canonical `paths.advisor.*` value.
 * Idempotent; unknown trailing segments pass through unchanged.
 */
export function resolveAdvisorNavRoute(route?: string): string | undefined {
  if (!route) return route;
  const segments = route.split('/').filter(Boolean);
  if (segments.length === 0) return route;
  const trailing = norm(segments[segments.length - 1]);
  // Ambiguous trailing segment: "ManagedAccounts" is both the new Configuration
  // section (paths.advisor.configuration.managedAccounts.index) AND the trailing
  // segment of the pre-existing Data Feeds sub-page
  // (/advisor/configuration/data-feeds/managed-accounts). Only the former should
  // be canonicalized here — the latter must pass through unchanged.
  if (
    trailing === 'managedaccounts' &&
    segments.some((segment) => norm(segment) === 'datafeeds')
  ) {
    return route;
  }
  // Symmetric case: the new Managed Accounts section's Data Feeds tab
  // (/advisor/configuration/managed-accounts/data-feeds) has a trailing
  // segment that normalizes to "datafeeds"; it must not canonicalize to the
  // unrelated legacy Data Feeds sub-page. It is already canonical — pass through.
  if (
    trailing === 'datafeeds' &&
    segments.some((segment) => norm(segment) === 'managedaccounts')
  ) {
    return route;
  }
  // The Assign & Edit Accounts deep route
  // (/advisor/configuration/managed-accounts/data-feeds/:feedId/accounts) ends
  // in "accounts", which the table would otherwise rewrite to the unrelated
  // Client's Wealth Accounts page — dropping the feed id (breaks the post-login
  // return-to). It is already canonical; pass it through.
  if (
    trailing === 'accounts' &&
    segments.some((segment) => norm(segment) === 'datafeeds')
  ) {
    return route;
  }
  // "BalanceSheet" is ambiguous: the Client's Wealth page and the legacy
  // Configuration "Balance Sheet Categories" item (/Setup/Configuration/BalanceSheet)
  // both end in it. Don't let the Configuration grant canonicalize to — and so
  // expose — the Client's Wealth Balance Sheet; pass it through untouched.
  if (
    trailing === 'balancesheet' &&
    segments.some((s) => {
      const seg = norm(s);
      return seg === 'configuration' || seg === 'setup';
    })
  ) {
    return route;
  }
  return ADVISOR_ROUTE_TABLE[trailing] ?? route;
}

/* -------------------------------------------------------------------------- */
/* Role-based visibility filtering                                            */
/* -------------------------------------------------------------------------- */

const normalizeHiddenRoute = (route: string): string =>
  route.replace(/\/$/, '').toLowerCase();

/**
 * TEMP(NU-940 / EUD-258): routes whose advisor pages are not implemented in
 * the frontend yet. The backend menu already emits them, so they are hidden
 * until each page ships — remove an entry in the same PR that ships its page.
 * Entries are canonical `paths.advisor.*` values; legacy `/Advisor/*` backend
 * shapes are covered because `isAdvisorRouteHidden` canonicalizes the route
 * via `resolveAdvisorNavRoute` before matching (so every legacy shape needs
 * an `ADVISOR_ROUTE_TABLE` entry).
 */
const NOT_YET_IMPLEMENTED_ROUTES: string[] = [
  // Business Intelligence section (entire) + leaves
  paths.advisor.businessIntelligence.index,
  paths.advisor.businessIntelligence.metrics,
  paths.advisor.businessIntelligence.aumHeldAway,
  paths.advisor.businessIntelligence.risks,
  // Clients — backend sends these under /Clients but no advisor page exists yet.
  paths.advisor.clients.activity,
  paths.advisor.clients.content,
  // Documents
  paths.advisor.reports,
  paths.advisor.documents.notes,
  paths.advisor.documents.intelligence,
  // Configuration
  paths.advisor.configuration.groupsAndUsers,
  paths.advisor.configuration.dataFeeds.managedAccounts,
];

/** Terms configuration is firm-only — permanently hidden for advisors. */
const FIRM_ONLY_ROUTES: string[] = [
  '/Advisor/Configuration/Terms',
  '/configuration/terms',
];

/**
 * All routes hidden from the Advisor role, normalized (lowercase, no trailing
 * slash). Shared between the sidebar and the shell tab navbar so both stay in
 * sync.
 */
export const ADVISOR_HIDDEN_ROUTES = new Set<string>(
  [...NOT_YET_IMPLEMENTED_ROUTES, ...FIRM_ONLY_ROUTES].map(
    normalizeHiddenRoute,
  ),
);

/** True when the normalized route is a hidden entry or a descendant of one. */
const matchesHiddenRoute = (route: string): boolean => {
  const normalized = normalizeHiddenRoute(route);
  if (ADVISOR_HIDDEN_ROUTES.has(normalized)) return true;
  // A hidden section hides its descendants (e.g. hiding
  // /advisor/business-intelligence also hides .../insights).
  for (const hidden of ADVISOR_HIDDEN_ROUTES) {
    if (normalized.startsWith(`${hidden}/`)) return true;
  }
  return false;
};

/**
 * True when a nav route is hidden for the Advisor role. Matches both the raw
 * route and its canonical form, so it works whether the backend sends a legacy
 * `/Advisor/*` shape or the route was already canonicalized. Descendants of a
 * hidden section count as hidden.
 */
export const isAdvisorRouteHidden = (route?: string): boolean => {
  if (!route) return false;
  const canonical = resolveAdvisorNavRoute(route) ?? route;
  return matchesHiddenRoute(route) || matchesHiddenRoute(canonical);
};

/**
 * Removes nav items the Advisor role should not see, recursing into children.
 * Items without a route pass through.
 *
 * A section emptied BY THIS FILTER (had children, all of them hidden) is
 * dropped too: keeping it would render an empty sidebar section, and its own
 * (canonicalized) route would land in `selectAdvisorAllowedRoutes.all` — a
 * grant for a section index whose sub-pages the backend never actually sent.
 * A node that already arrived with empty children (backend leaf serialized as
 * `Items: []`) is a legitimate grant and passes through unchanged.
 *
 * Use anywhere tabs/links are derived from the backend menu so they match
 * the sidebar.
 */
export function filterNavItemsForAdvisor(
  nodes: NavigationItem[],
): NavigationItem[] {
  return nodes
    .filter((node) => !isAdvisorRouteHidden(node.route))
    .flatMap((node) => {
      if (!node.children || node.children.length === 0) return [{ ...node }];
      const children = filterNavItemsForAdvisor(node.children);
      if (children.length === 0) return [];
      return [{ ...node, children }];
    });
}

/* -------------------------------------------------------------------------- */
/* Clients / Relationships section shape                                      */
/* -------------------------------------------------------------------------- */

/**
 * Decide the final shape of the Clients section AFTER the hide filter has run.
 *
 * The backend sends Clients as a parent whose children include a
 * self-referential `/Clients` section-index child plus real sub-pages
 * (Prospects, etc.). Whether the section renders as a "Relationships" dropdown
 * or a plain "Clients" leaf depends on whether any REAL sub-page is enabled —
 * a decision that can only be made once the hide filter has removed
 * not-yet-implemented sub-pages, and that must ignore the self-referential
 * index child (which always survives because `/advisor/clients` is not hidden).
 *
 *  - At least one non-index child survives (e.g. Prospects enabled): keep the
 *    dropdown and label the parent "Relationships".
 *  - Only the self-referential index child survives (Prospects disabled):
 *    collapse to a single "Clients" leaf that links straight to the section
 *    index, so there is no redundant one-item dropdown.
 *
 * A childless Clients grant passes through untouched — it is already a leaf.
 * Identification is by route (not label) so it is robust to any upstream
 * relabeling.
 */
export function finalizeAdvisorClientsSection(
  nodes: NavigationItem[],
): NavigationItem[] {
  const indexRoute = normalizePath(paths.advisor.clients.index);
  return nodes.map((node) => {
    if (normalizePath(node.route) !== indexRoute) return node;
    if (!node.children || node.children.length === 0) return node;

    const nonIndexChildren = node.children.filter(
      (child) => normalizePath(child.route) !== indexRoute,
    );

    if (nonIndexChildren.length > 0) {
      return { ...node, text: 'Relationships' };
    }

    // Only the self-referential index child left — collapse to a Clients leaf.
    return {
      ...node,
      text: 'Clients',
      route: paths.advisor.clients.index,
      children: undefined,
    };
  });
}

export default resolveAdvisorNavRoute;
